import { Container, Graphics, Text } from 'pixi.js';
import type { Disposable } from '../../core/Event';
import { Easings, TweenGroup } from '../../core/Tween';
import { DefaultRandom, type Random } from '../../core/Random';
import { PickEmModel } from '../../model/pickem/PickEmModel';
import type { PickEmPrize } from '../../model/pickem/PickEmTypes';
import { PrimaryButton } from '../../widgets/PrimaryButton';
import { PickEmGrid } from '../../widgets/pickem/PickEmGrid';
import type { Fragment } from '../Fragment';
import type { SceneLayoutContext } from '../SceneComponent';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface PickEmFragmentOptions {
  accentColor?: number;
  warmColor?: number;
  /** Box accent (overrides theme accent if set). Default = accentColor. */
  boxAccentColor?: number;
}

export interface PickEmFragmentInput {
  /** Headline shown above the grid — "INTERROGATE THE SUSPECT", etc. */
  title: string;
  /** Sub-title shown above the grid — "Pick 3 alibis to find evidence." */
  subtitle: string;
  /** Prize pool — same shape as {@link PickEmModel}'s constructor. Must
   * have at least {@code boxCount} entries; extra entries are ignored. */
  prizes: readonly PickEmPrize[];
  /** Number of boxes shown. Default 9. */
  boxCount?: number;
  /** Picks the player can make. Default 3. */
  picksAllowed?: number;
  /** Boxes per row. Default ceil(sqrt(boxCount)). */
  columnCount?: number;
}

/**
 * Pick-Em as a fragment overlay — same model + grid widgets as the
 * standalone {@link ../PickEmDemoScene PickEmDemoScene}, presented
 * inside a dim + panel modal so it can be triggered from any host
 * scene (board's bonus tile, slot's scatter, etc.).
 *
 * <p>Each call to {@link present} creates a fresh {@link PickEmModel}
 * and grid, runs the picks, and resolves with the total winnings the
 * player accumulated. The host scene credits those winnings to its
 * own model — the fragment doesn't know about the host's wallet.
 */
export class PickEmFragment extends Container implements Fragment {
  private readonly dim: Graphics;
  private readonly panel: Graphics;
  private readonly title: Text;
  private readonly subtitle: Text;
  private readonly statusLabel: Text;
  private readonly doneButton: PrimaryButton;
  private readonly tweens = new TweenGroup();
  private readonly random: Random;
  private readonly options: PickEmFragmentOptions;
  private grid: PickEmGrid | null = null;
  private model: PickEmModel | null = null;
  private subscriptions: Disposable[] = [];
  private resolveResult: ((winnings: number) => void) | null = null;
  private autoDismissTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(options: PickEmFragmentOptions = {}, random: Random = new DefaultRandom()) {
    super();
    this.options = options;
    this.random = random;
    this.alpha = 0;
    this.visible = false;

    this.dim = new Graphics();
    this.dim.eventMode = 'static';
    // Note: tap-on-dim does NOT dismiss — players have to use all their
    // picks (or wait for the auto-finish). Otherwise they could short-
    // circuit the bonus.
    this.addChild(this.dim);

    this.panel = new Graphics();
    this.addChild(this.panel);

    this.title = this.buildText('', 18, 0xffffff, '900', 2);
    this.subtitle = this.buildText('', 13, 0xffffff, '500', 0.5);
    this.subtitle.alpha = 0.7;
    this.statusLabel = this.buildText('', 14, 0xffffff, '700', 1);
    this.statusLabel.alpha = 0.85;

    this.doneButton = new PrimaryButton({
      label: 'COLLECT',
      width: 180,
      fillActive: options.warmColor ?? 0xfff59d,
      fillHover: 0x6b6020,
      textColor: 0x1a0830,
      textSize: 14,
      letterSpacing: 1,
      onTap: () => this.dismissWithResult(),
    });
    this.doneButton.visible = false;

    this.addChild(this.title, this.subtitle, this.statusLabel, this.doneButton);
  }

  layout(ctx: SceneLayoutContext): void {
    this.dim.clear();
    this.dim.rect(0, 0, ctx.width, ctx.height).fill({ color: 0x05010f, alpha: 0.78 });

    const accent = this.options.accentColor ?? 0xff6bd6;
    const panelWidth = Math.min(420, Math.floor(ctx.width * 0.92));
    const panelHeight = 460;
    const panelX = Math.floor((ctx.width - panelWidth) / 2);
    const panelY = Math.floor((ctx.height - panelHeight) / 2);
    this.panel.clear();
    this.panel
      .roundRect(panelX, panelY, panelWidth, panelHeight, 18)
      .fill({ color: 0x1a0830, alpha: 0.96 })
      .stroke({ color: accent, alpha: 0.95, width: 3 });

    const cx = panelX + panelWidth / 2;
    this.title.x = cx;
    this.title.y = panelY + 24;
    this.subtitle.x = cx;
    this.subtitle.y = panelY + 56;

    if (this.grid) {
      // Grid sits centred in the middle of the panel.
      const naturalGridW = this.grid.totalWidth();
      const naturalGridH = this.grid.totalHeight();
      const maxGridW = panelWidth - 32;
      const maxGridH = panelHeight - 180;
      const gridScale = Math.min(1, maxGridW / naturalGridW, maxGridH / naturalGridH);
      this.grid.scale.set(gridScale);
      const scaledW = naturalGridW * gridScale;
      this.grid.x = cx - scaledW / 2;
      this.grid.y = panelY + 84;
    }

    this.statusLabel.x = cx;
    this.statusLabel.y = panelY + panelHeight - 84;
    this.doneButton.x = cx;
    this.doneButton.y = panelY + panelHeight - 44;
  }

  /** Open the fragment with a fresh Pick-Em round. Resolves with the
   * total coin winnings the player accumulated. The host scene is
   * responsible for crediting those coins to its own balance model. */
  present(input: PickEmFragmentInput): Promise<number> {
    this.cleanupRound();

    const boxCount = input.boxCount ?? 9;
    const picksAllowed = input.picksAllowed ?? 3;
    const columnCount = input.columnCount ?? Math.ceil(Math.sqrt(boxCount));
    this.model = new PickEmModel(
      {
        boxCount,
        picksAllowed,
        prizes: input.prizes,
        columnCount,
      },
      this.random,
    );

    const accent = this.options.boxAccentColor ?? this.options.accentColor ?? 0xff6bd6;
    const warm = this.options.warmColor ?? 0xfff59d;
    this.grid = new PickEmGrid({
      boxCount,
      columnCount,
      accentColor: accent,
      defaultRevealColor: warm,
      onPick: (index) => void this.handlePick(index),
    });
    this.addChildAt(this.grid, this.children.length - 4); // below status + buttons

    this.title.text = input.title;
    this.subtitle.text = input.subtitle;
    this.statusLabel.text = `${picksAllowed} pick${picksAllowed === 1 ? '' : 's'} to make`;
    this.doneButton.visible = false;

    this.subscriptions.push(
      this.model.events.on('box-revealed', (event) => {
        const remaining = event.picksRemaining;
        const total = this.model?.totalWinnings() ?? 0;
        this.statusLabel.text =
          remaining > 0
            ? `+${event.prize.value} — ${remaining} pick${remaining === 1 ? '' : 's'} left · ${total} banked`
            : `final reveal — ${total} banked`;
      }),
      this.model.events.on('pick-em-complete', (event) => {
        this.statusLabel.text = `🏁 ${event.totalWinnings.toLocaleString()} won — tap COLLECT`;
        this.doneButton.visible = true;
        // Belt-and-braces auto-dismiss in case the player wanders away.
        if (this.autoDismissTimer) clearTimeout(this.autoDismissTimer);
        this.autoDismissTimer = setTimeout(() => this.dismissWithResult(), 4000);
      }),
    );

    // Re-layout now that the grid exists.
    this.refreshLayoutFromCachedViewport();
    this.fadeIn();

    return new Promise<number>((resolve) => {
      this.resolveResult = resolve;
    });
  }

  destroy(): void {
    this.cleanupRound();
    this.tweens.cancelAll();
    if (this.autoDismissTimer) clearTimeout(this.autoDismissTimer);
    this.autoDismissTimer = null;
    if (this.resolveResult) {
      // Fragment torn down before the player finished — hand back zero so
      // the host promise doesn't hang.
      this.resolveResult(0);
      this.resolveResult = null;
    }
    super.destroy({ children: true });
  }

  // -------- private --------

  private async handlePick(index: number): Promise<void> {
    if (!this.model || !this.grid) return;
    const prize = this.model.pick(index);
    if (!prize) return;
    await this.grid.reveal(index, prize);
  }

  private dismissWithResult(): void {
    if (this.autoDismissTimer) {
      clearTimeout(this.autoDismissTimer);
      this.autoDismissTimer = null;
    }
    const winnings = this.model?.totalWinnings() ?? 0;
    this.tweens.start({
      durationMs: 220,
      easing: Easings.easeOutCubic,
      onUpdate: (p) => {
        this.alpha = 1 - p;
      },
      onComplete: () => {
        this.visible = false;
        this.cleanupRound();
        const resolver = this.resolveResult;
        this.resolveResult = null;
        if (resolver) resolver(winnings);
      },
    });
  }

  private fadeIn(): void {
    this.visible = true;
    this.scale.set(0.92);
    this.tweens.start({
      durationMs: 280,
      easing: Easings.easeOutBack,
      onUpdate: (p) => {
        this.alpha = p;
        this.scale.set(0.92 + p * 0.08);
      },
    });
  }

  private cleanupRound(): void {
    for (const sub of this.subscriptions) sub.dispose();
    this.subscriptions = [];
    if (this.grid) {
      this.grid.destroyGrid();
      this.removeChild(this.grid);
      this.grid.destroy({ children: true });
      this.grid = null;
    }
    if (this.model) {
      this.model.dispose();
      this.model = null;
    }
  }

  /** Recompute layout using the most recently received SceneLayoutContext.
   * Called after a new round's grid is constructed so it positions
   * correctly without waiting for the next resize. */
  private refreshLayoutFromCachedViewport(): void {
    // The host scene calls layout() before each present(), so we can
    // re-trigger by reaching into the dim's bounds (already drawn at the
    // viewport size). This avoids stashing a separate copy of the ctx.
    const bounds = this.dim.getLocalBounds();
    if (bounds.width === 0 || bounds.height === 0) return;
    this.layout({
      width: bounds.width,
      height: bounds.height,
      centerX: bounds.width / 2,
      machineTop: 0,
      machineBottom: bounds.height,
      spinButtonY: bounds.height,
    });
  }

  private buildText(
    text: string,
    size: number,
    fill: number,
    weight: '500' | '700' | '900' = '700',
    letterSpacing: number = 0,
  ): Text {
    const t = new Text({
      text,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: size,
        fontWeight: weight,
        fill,
        align: 'center',
        letterSpacing,
      },
    });
    t.anchor.set(0.5);
    return t;
  }
}
